import { WithStyles } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import HelpIcon from '@material-ui/icons/Help';
import HighlightOff from '@material-ui/icons/HighlightOff';
import MenuIcon from '@material-ui/icons/Menu';
import { push } from 'connected-react-router';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { UpwardTransition } from 'src/utils';
import {
	ELearningKind,
	openELearningHint,
	openELearningIndexTree,
	setELearningPage,
} from '../actions/eLearning';
import ELearningHint from '../components/ELearningHint';
import { IRootState } from '../reducers';
import i18n from '../resources/i18n';
import ScenarioFactory, {
	IFetchELearningProgressParam,
	IUpdateELearningUserBookMarkParam,
} from '../scenario/ELearningScenarioFactory';
import ScormAPIS from '../utils/ScormAPI';
import { funcClose } from '../utils/StartupUtils';
import { decodeHTMLEntities } from '../utils/Text';

const styles = (theme: Theme) =>
	createStyles({
		menuButton: {
			marginLeft: -12,
			marginRight: 20,
		},
		progressBar: {
			backgroundColor: '#dadada',
			borderRadius: '2px',
			margin: 4,
			[theme.breakpoints.down('xs')]: {
				height: 10,
				width: 30,
			},
			[theme.breakpoints.up('sm')]: {
				height: 16,
				width: 60,
			},
		},
		progressText: {
			fontSize: '0.8rem',
		},
		root: {
			flexGrow: 1,
		},
		title: {
			flex: 1,
			[theme.breakpoints.down('sm')]: {
				fontSize: '1rem',
			},
		},
	});

interface IState {
	isShowConfirmFinish: boolean;
}

/**
 * 受講画面の上部バーコンポーネント
 */
class EBar extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			isShowConfirmFinish: false,
		};
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		// ProgressBlockデータ取得
		this.props.fetchELearningProgress(this.props.token, {
			careerID: this.props.careerID,
			classID: this.props.classID,
			isPreview: this.props.preview,
			userId: this.props.userAttr.userId,
		});
	}

	/**
	 * render
	 */
	public render() {
		const {
			classes,
			progress,
			currentTreeItem,
			preview,
			userAttr,
		} = this.props;

		let topicID = '';
		let hint = false;
		let bookmark;
		if (currentTreeItem) {
			topicID = currentTreeItem.id;
			if (
				currentTreeItem.hintList &&
				currentTreeItem.hintList[userAttr.lang]
			) {
				hint =
					currentTreeItem.hintList[userAttr.lang].find(
						(e: number) => e === this.props.currentPage
					) !== undefined;
			}
			if (currentTreeItem.kind === ELearningKind.TEXT) {
				const icon =
					this.props.currentPage === currentTreeItem.userBookMark;
				bookmark = (
					<IconButton
						color="inherit"
						onClick={this.handleUpdateBookmark}
					>
						{icon ? <BookmarkIcon /> : <BookmarkBorderIcon />}
					</IconButton>
				);
			}
		}

		return (
			<AppBar position="static" color="default" className={classes.root}>
				<Toolbar>
					<IconButton
						className={classes.menuButton}
						color="inherit"
						aria-label="Menu"
						onClick={this.handleOpenIndexTree}
					>
						<MenuIcon />
					</IconButton>
					<Typography
						variant="h6"
						color="inherit"
						noWrap={true}
						className={classes.title}
					>
						{decodeHTMLEntities(this.props.careerTitle)}
					</Typography>

					{hint && (
						<IconButton
							color="inherit"
							onClick={this.handleOpenHint}
						>
							<HelpIcon />
						</IconButton>
					)}
					{hint && (
						<ELearningHint
							topicID={topicID}
							page={this.props.currentPage}
							lang={this.props.userAttr.lang}
							careerID={this.props.careerID}
							open={this.props.isOpenHint}
							onClose={this.handleCloseHint}
							preview={this.props.preview}
						/>
					)}

					{preview || bookmark}
					{!preview && !isNaN(progress) && (
						<>
							<Hidden xsDown={true}>
								<Typography variant="subtitle1" color="inherit">
									{i18n.t('Ebar_0001')}
								</Typography>
							</Hidden>
							<div className={classes.progressBar}>
								<div
									style={{
										backgroundColor:
											progress > 66
												? '#85cc00'
												: progress > 33
												? '#ffbf00'
												: '#ff2e00',
										borderRadius: '2px',
										height: '100%',
										transition: 'all .2s ease-out',
										width: `${progress}%`,
									}}
								/>
							</div>
							<Typography
								variant="subtitle1"
								color="inherit"
								className={classes.progressText}
							>
								{progress}%
							</Typography>
						</>
					)}
					<Button
						onClick={this.handleOpenConfirmFinish}
						color="inherit"
					>
						<HighlightOff />
						<Hidden xsDown={true}>
							{preview
								? i18n.t('Ebar_0002')
								: i18n.t('Ebar_0003')}
						</Hidden>
					</Button>

					<Dialog
						open={this.state.isShowConfirmFinish ? true : false}
						TransitionComponent={UpwardTransition}
						keepMounted={true}
						onClose={this.handleCloseConfirmFinish}
						aria-labelledby="alert-dialog-slide-title"
						aria-describedby="alert-dialog-slide-description"
					>
						<DialogTitle id="alert-dialog-slide-title">
							{i18n.t('Ebar_0004')}
						</DialogTitle>
						<DialogContent>
							{preview ? (
								<DialogContentText id="alert-dialog-slide-description">
									{i18n.t('Ebar_0005')}
								</DialogContentText>
							) : (
								<DialogContentText id="alert-dialog-slide-description">
									{i18n.t('Ebar_0006')}
								</DialogContentText>
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleFinish} color="primary">
								{i18n.t('Ebar_0007')}
							</Button>
							<Button onClick={this.handleCloseConfirmFinish}>
								{i18n.t('Ebar_0008')}
							</Button>
						</DialogActions>
					</Dialog>
				</Toolbar>
			</AppBar>
		);
	}

	/**
	 * 受講終了ハンドラ
	 */
	protected handleFinish = () => {
		funcClose();
		ScormAPIS.TelsFlashNavigation(this.props.scormApi);
		this.setState({ isShowConfirmFinish: false });
		if (this.props.preview) {
			// eラーニング閲覧のとき
			if (window.opener && window.opener.closed === false) {
				// 呼び出し元ウィンドウが生きている場合はそちらで処理してもらう
				window.opener.postMessage('endpreview', '*');
			}
			return;
		}
		this.props.pushHistory(this.props.caller);
	};

	/**
	 * 受講終了確認ダイアログオープンハンドラ
	 */
	protected handleOpenConfirmFinish = () => {
		this.setState({ isShowConfirmFinish: true });
	};

	/**
	 * 受講終了確認ダイアログクローズハンドラ
	 */
	protected handleCloseConfirmFinish = () => {
		this.setState({ isShowConfirmFinish: false });
	};

	/**
	 * インデックスツリーオープンハンドラ
	 */
	protected handleOpenIndexTree = () => {
		this.props.openELearningIndexTree();
	};

	/**
	 * ヒントオープンハンドラ
	 */
	protected handleOpenHint = () => {
		this.props.openELearningHint(true);
	};

	/**
	 * ヒント画面クローズハンドラ
	 */
	protected handleCloseHint = () => {
		this.props.openELearningHint(false);
	};

	/**
	 * ブックマーク更新ハンドラ
	 */
	protected handleUpdateBookmark = () => {
		const topicID = this.props.currentTreeItem
			? this.props.currentTreeItem.id
			: '';

		this.props.updateELearningUserBookMark(this.props.token, {
			careerID: this.props.careerID,
			classID: this.props.classID,
			page: this.props.currentPage,
			topicID,
			userAttr: this.props.userAttr,
		});
	};
}

const mapStateToProps = (state: IRootState) => ({
	careerTitle: state.eLearning.careerTitle,
	currentPage: state.eLearning.currentPage,
	currentTreeItem: state.eLearning.currentTreeItem,
	indexTree: state.eLearning.indexTree,
	isOpenHint: state.eLearning.isOpenHint,
	progress: state.eLearning.progress,
	scormApi: state.eLearning.scormApi,
	startTime: state.eLearning.startTime,

	token: state.session.token,
	userAttr: state.session.userAttr,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	fetchELearningProgress: (
		token: string,
		arg: IFetchELearningProgressParam
	) => {
		const s = ScenarioFactory.createFetchELearningProgress(dispatch, token);
		s.run(arg);
	},
	openELearningHint: (isOpen: boolean) =>
		dispatch(openELearningHint({ isOpen })),
	openELearningIndexTree: () =>
		dispatch(openELearningIndexTree({ isOpen: true })),
	pushHistory: (path: string) => dispatch(push(path)),
	setELearningPage: (page: number) => dispatch(setELearningPage({ page })),
	updateELearningUserBookMark: (
		token: string,
		arg: IUpdateELearningUserBookMarkParam
	) => {
		const s = ScenarioFactory.createUpdateELearningUserBookMark(
			dispatch,
			token
		);
		s.run(arg);
	},
});

export interface IEBarProps {
	careerID: string;
	classID: string;
	caller: string;
	preview: boolean;
}

const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

type IStateProps = ReturnType<typeof mapStateToProps>;
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
type IOwnProps = WithStyles<typeof styles> & IEBarProps;
type IProps = ReturnType<typeof mergeProps>;

const Connected = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(EBar);

const Styled = withStyles(styles)(Connected);

export default Styled;



// WEBPACK FOOTER //
// ./src/containers/EBar.tsx